import classNames from 'classnames';
import Image from '@/components/atoms/image/image';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import Accordion, {
  AccordionProps,
} from '@/components/organisms/accordion/accordion';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Row from '@/components/utilities/row/row';
import styles from './accordion-block.module.scss';

export interface AccordionBlockProps {
  /** Indicates where to position the text */
  alignment?: 'left' | 'right';
  /** The class name to apply */
  className?: string;
  /** The text to show as a headline */
  headline?: string | null;
  /** The image to show */
  image?: Content.Properties.ImageWithAspectRatio | null;
  /** The text to show as a caption */
  imageCaption?: string | null;
  /** The list of accordion items to show */
  items?: AccordionProps['items'];
  /** The text to show as a pre headline */
  preHeadline?: string | null;
  /** The text to show as a sub headline */
  subHeadline?: string | null;
  /** The rich text to show as a description */
  text?: string | null;
}

const AccordionBlock = ({
  alignment = 'left',
  className,
  headline,
  image,
  imageCaption,
  items = [],
  preHeadline,
  subHeadline,
  text,
}: AccordionBlockProps) => {
  const hasText = headline || preHeadline || subHeadline || text;

  const renderContent = () => (
    <div className={styles.contentWrapper}>
      {(headline || preHeadline) && (
        <div className={styles.headlineWrapper}>
          {preHeadline && (
            <Typography
              className={styles.preHeadline}
              tag="small"
              tagStyle="labelMedium"
            >
              {preHeadline}
            </Typography>
          )}

          {headline && (
            <Typography
              className={styles.headline}
              tag="h2"
              tagStyle="displayMedium"
            >
              {headline}
            </Typography>
          )}
        </div>
      )}

      {subHeadline && (
        <Typography
          className={styles.subHeadline}
          tag="h3"
          tagStyle="bodyLargeBold"
        >
          {subHeadline}
        </Typography>
      )}

      {text && <RichText text={text} />}
    </div>
  );

  const renderImage = () => (
    <div className={styles.imageWrapper}>
      {image.url && (
        <Image
          alt={image.altText}
          aspectRatio={image.aspectRatio}
          className={styles.image}
          focalPoint={image.focalPoint}
          src={image.url}
          srcSet={{
            desktop: {
              width: 515,
            },
            laptop: {
              width: 355,
            },
            tablet: {
              width: 672,
            },
          }}
          unoptimized
          width={296}
        />
      )}

      {imageCaption && (
        <Typography className={styles.imageCaption} tagStyle="labelMedium">
          {imageCaption}
        </Typography>
      )}
    </div>
  );

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isRightAligned]: alignment === 'right',
      })}
    >
      <Container>
        <Row className={styles.row}>
          {(hasText || image) && (
            <Column
              offset={{ laptop: alignment === 'right' ? (!image ? 2 : 1) : 0 }}
              order={{ laptop: alignment === 'right' ? 1 : 2 }}
              width={{ laptop: !image ? 4 : 5 }}
            >
              {image ? renderImage() : renderContent()}
            </Column>
          )}

          <Column
            className={styles.contentColumn}
            offset={{
              laptop: (hasText || image) && alignment === 'right' ? 0 : 2,
            }}
            order={{ laptop: 1 }}
            width={{ laptop: hasText || image ? 4 : 8 }}
          >
            {hasText && image && renderContent()}

            <Accordion items={items} />
          </Column>
        </Row>
      </Container>
    </div>
  );
};

AccordionBlock.displayName = 'AccordionBlock';

export default AccordionBlock;
